import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Settings, Plus, X } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const CommonIssuesAdminPage = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newIssueText, setNewIssueText] = useState("");
  const [newIssueSeverity, setNewIssueSeverity] = useState("amber");
  const [stats, setStats] = useState({ total: 0, database_count: 0, hardcoded_count: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    try {
      const res = await axios.get(`${API}/admin/common-issues`);
      setIssues(res.data.issues);
      setStats({
        total: res.data.total,
        database_count: res.data.database_count,
        hardcoded_count: res.data.hardcoded_count
      });
    } catch (err) {
      console.error("Failed to fetch common issues:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectIssue = async (itemKey) => {
    try {
      const res = await axios.get(`${API}/admin/common-issues/${itemKey}`);
      setSelectedIssue(res.data);
      setEditMode(false);
    } catch (err) {
      console.error("Failed to fetch issue:", err);
    }
  };

  const handleAddIssue = async () => {
    if (!newIssueText.trim() || !selectedIssue) return;
    
    try {
      await axios.post(
        `${API}/admin/common-issues/${selectedIssue.item_key}/add-issue?severity=${newIssueSeverity}&issue_text=${encodeURIComponent(newIssueText)}`
      );
      setNewIssueText("");
      handleSelectIssue(selectedIssue.item_key);
      fetchIssues();
    } catch (err) {
      alert("Failed to add issue");
    }
  };

  const handleRemoveIssue = async (severity, issueText) => {
    if (!confirm(`Remove "${issueText}"?`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/common-issues/${selectedIssue.item_key}/remove-issue?severity=${severity}&issue_text=${encodeURIComponent(issueText)}`
      );
      handleSelectIssue(selectedIssue.item_key);
      fetchIssues();
    } catch (err) {
      alert("Failed to remove issue");
    }
  };

  const filteredIssues = issues.filter(issue => 
    issue.item_key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (issue.display_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-6 py-4">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/foh")}>
              ← Back to Dashboard
            </Button>
            <h1 className="text-xl font-semibold">Common Issues Library</h1>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>{stats.total} items</span>
            <Badge variant="outline">{stats.database_count} customized</Badge>
            <Badge variant="outline" className="text-gray-400">{stats.hardcoded_count} default</Badge>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Panel - Issue List */}
          <div className="col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search issues..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-issues-input"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                {filteredIssues.map(issue => (
                  <button
                    key={issue.item_key}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition-colors ${
                      selectedIssue?.item_key === issue.item_key ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
                    }`}
                    onClick={() => handleSelectIssue(issue.item_key)}
                    data-testid={`issue-${issue.item_key}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">{issue.display_name || issue.item_key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                        <p className="text-xs text-gray-500">{issue.item_key}</p>
                      </div>
                      <div className="flex gap-1">
                        {issue.red?.length > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-xs">{issue.red.length}</Badge>
                        )}
                        {issue.amber?.length > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">{issue.amber.length}</Badge>
                        )}
                      </div>
                    </div>
                    {issue.source === "database" && (
                      <Badge className="mt-1 bg-blue-100 text-blue-700 text-xs">Customized</Badge>
                    )}
                  </button>
                ))}
                {filteredIssues.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No issues found</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Issue Details */}
          <div className="col-span-2">
            {selectedIssue ? (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{selectedIssue.display_name || selectedIssue.item_key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</CardTitle>
                      <CardDescription className="font-mono text-xs">{selectedIssue.item_key}</CardDescription>
                    </div>
                    {selectedIssue.source === "database" && (
                      <Badge className="bg-blue-100 text-blue-700">Customized</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Red Issues */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <h3 className="font-semibold text-red-800">Safety Critical Issues ({selectedIssue.red?.length || 0})</h3>
                    </div>
                    <div className="space-y-2">
                      {selectedIssue.red?.map((issue, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-red-50 border border-red-200 rounded px-3 py-2">
                          <span className="text-sm">{issue}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveIssue("red", issue)}
                            className="text-red-600 hover:text-red-800 h-6 w-6 p-0"
                            data-testid={`remove-red-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(!selectedIssue.red || selectedIssue.red.length === 0) && (
                        <p className="text-sm text-gray-500 italic">No red issues defined</p>
                      )}
                    </div>
                  </div>

                  {/* Amber Issues */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <h3 className="font-semibold text-amber-800">Advisory Issues ({selectedIssue.amber?.length || 0})</h3>
                    </div>
                    <div className="space-y-2">
                      {selectedIssue.amber?.map((issue, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-amber-50 border border-amber-200 rounded px-3 py-2">
                          <span className="text-sm">{issue}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveIssue("amber", issue)}
                            className="text-amber-600 hover:text-amber-800 h-6 w-6 p-0"
                            data-testid={`remove-amber-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(!selectedIssue.amber || selectedIssue.amber.length === 0) && (
                        <p className="text-sm text-gray-500 italic">No amber issues defined</p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Add New Issue */}
                  <div>
                    <h3 className="font-semibold mb-3">Add New Issue</h3>
                    <div className="flex gap-2">
                      <Select value={newIssueSeverity} onValueChange={setNewIssueSeverity}>
                        <SelectTrigger className="w-32" data-testid="new-issue-severity">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="red">🔴 Red</SelectItem>
                          <SelectItem value="amber">🟠 Amber</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Enter issue description..."
                        value={newIssueText}
                        onChange={(e) => setNewIssueText(e.target.value)}
                        className="flex-1"
                        data-testid="new-issue-text"
                        onKeyDown={(e) => e.key === "Enter" && handleAddIssue()}
                      />
                      <Button onClick={handleAddIssue} disabled={!newIssueText.trim()} data-testid="add-issue-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600">Select an Issue</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Choose an inspection item from the list to view and edit its common issues.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonIssuesAdminPage;
